import { useEffect, useState } from "react";
import { Bot, History, GitBranch } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const AREAS = ["language_accuracy", "naturalness", "cefr_fit", "answer_key", "instructions", "pedagogy", "culture_stereotypes", "duplication", "formatting_script", "provenance", "objective_alignment", "teacher_learner_match"];
const SEV = { high: "bg-coral text-white", medium: "bg-butter", low: "bg-white" };
const err = (e, t) => toast.error(e?.response?.data?.detail || t("al.failed"));

const TIERS = ["blocking", "review", "advisory"];
const TIER_C = { blocking: "bg-coral text-white", review: "bg-butter", advisory: "bg-white" };
const STATUS_C = { ready_for_human_review: "bg-mint", blocking_issues_remain: "bg-coral text-white", needs_human_attention: "bg-butter" };

function Finding({ f, i }) {
  const { t } = useI18n();
  return (
    <div className="text-xs flex items-start gap-1.5" data-testid={`rv-finding-${f.fid || i}`}>
      <span className={`nb-tag text-[10px] shrink-0 ${TIER_C[f.tier]}`}>{t(`al.ck2.tier.${f.tier}`)}</span>
      <span className="min-w-0"><b>{AREAS.includes(f.area) ? t(`al.p.area.${f.area}`) : f.area}</b>
        {f.source === "rule" && <span className="nb-tag bg-sky text-[9px] ms-1">{t("al.ck2.rule")}</span>}
        {f.status === "carried" && <span className="nb-tag bg-white text-[9px] ms-1">{t("al.ck2.carried")}</span>}
        {f.status === "new" && f.mode === "recheck" && <span className="nb-tag bg-white text-[9px] ms-1">{t("al.ck2.new")}</span>}
        {f.path && <code className="text-gray-500 ms-1 break-all" dir="ltr">{f.path}</code>} <span dir="auto">{f.note}</span></span>
    </div>
  );
}

function Tiered({ pc }) {
  const { t } = useI18n();
  return (
    <>
      {pc.ai_status && <div className="text-sm" data-testid="rv-ai-status"><span className={`nb-tag text-xs ${STATUS_C[pc.ai_status]}`}>{t(`al.ck2.status.${pc.ai_status}`)}</span>
        <span className="text-xs text-gray-600 ms-2" dir="ltr">{TIERS.map((x) => `${pc.counts?.[x] ?? 0} ${t(`al.ck2.tier.${x}`)}`).join(" · ")}{pc.resolved?.length ? ` · ${pc.resolved.length} ${t("al.ck2.resolved")}` : ""}</span></div>}
      {pc.inconsistencies?.length > 0 && <div className="nb-card bg-lavender p-2 text-xs space-y-1" data-testid="rv-inconsistency"><b>{t("al.ck2.incons")}</b>
        {pc.inconsistencies.map((x, i) => <div key={i} dir="auto"><code dir="ltr">{x.path}</code><p><s>{String(x.original)}</s> → {String(x.proposed)}</p><p>1 · {x.first_rationale}</p><p>2 · {x.second_rationale}</p></div>)}</div>}
      {TIERS.map((tier) => {
        const fs = pc.findings.filter((f) => f.tier === tier);
        if (!fs.length) return null;
        const body = fs.map((f, i) => <Finding key={f.fid || i} f={{ ...f, mode: pc.mode }} i={i} />);
        return tier === "blocking" ? <div key={tier} className="space-y-1">{body}</div> : <details key={tier} open={tier === "review"} className="space-y-1" data-testid={`rv-tier-${tier}`}><summary className="text-xs font-bold cursor-pointer">{t(`al.ck2.tier.${tier}`)} ({fs.length})</summary>{body}</details>;
      })}
      {pc.resolved?.length > 0 && <details className="text-xs text-gray-600" data-testid="rv-resolved"><summary className="cursor-pointer">{t("al.ck2.resolved")} ({pc.resolved.length})</summary>{pc.resolved.map((r, i) => <p key={i} dir="auto">✓ {r.ref} {r.note}</p>)}</details>}
    </>
  );
}

// Advisory AI pre-review: it never changes status or content; the human decides.
export function PrecheckPanel({ a, onRan }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const pc = a.precheck;
  const run = async () => { setBusy(true); try { await api.post(`/studio/assets/${a.asset_id}/precheck`); onRan(); } catch (e) { err(e, t); } finally { setBusy(false); } };
  return (
    <div className="nb-card bg-sky/40 p-3 space-y-1.5" data-testid="rv-precheck">
      <div className="flex items-center justify-between gap-2"><b className="flex items-center gap-1.5"><Bot className="w-4 h-4" /> {t("al.p.precheck")}</b>
        {["draft", "reviewed"].includes(a.status) && !a.revision_of && !["in_review", "recheck", "blocked"].includes(a.editorial?.stage) && <button onClick={run} disabled={busy} className="nb-tag bg-white text-xs" data-testid="rv-precheck-run">{busy ? t("al.p.generating") : t(pc ? "al.p.precheckRerun" : "al.p.precheckRun")}</button>}</div>
      {!pc ? <p className="text-xs text-gray-600">{t("al.p.precheckNone")}</p> : (
        <>
          <div className="text-sm" data-testid="rv-precheck-verdict">{!pc.ai_status && <span className="nb-tag bg-white text-xs">{t(`al.p.pv.${pc.verdict}`)}</span>} <span dir="auto">{pc.summary}</span>
            {pc.version !== a.version && <span className="text-xs text-coral"> · {t("al.p.precheckStale")}</span>}</div>
          {pc.findings ? <Tiered pc={pc} /> : pc.flags.map((f, i) => <div key={i} className="text-xs flex items-start gap-1.5" data-testid={`rv-flag-${i}`}><span className={`nb-tag text-[10px] shrink-0 ${SEV[f.severity]}`}>{t(`al.p.sev.${f.severity}`)}</span><span className="min-w-0"><b>{AREAS.includes(f.area) ? t(`al.p.area.${f.area}`) : f.area}</b> <span dir="auto">{f.note}</span></span></div>)}
          {pc.proposed_corrections.length > 0 && <details className="text-xs" data-testid="rv-corrections"><summary className="cursor-pointer font-bold">{t("al.p.corrections", { n: pc.proposed_corrections.length })}</summary>
            {pc.proposed_corrections.map((c, i) => <p key={i} className="mt-1"><b>{c.field}:</b> <s dir="auto">{c.current}</s> → <span dir="auto">{c.proposed}</span> <i className="text-gray-600">({c.why})</i></p>)}</details>}
          <p className="text-[11px] text-gray-500">{t("al.p.precheckAdvisory")} · {pc.model}</p>
        </>
      )}
    </div>
  );
}

export function ContentEditor({ a, lang, onSaved }) {
  const { t } = useI18n();
  const [txt, setTxt] = useState(null);
  const save = async () => {
    let content; try { content = JSON.parse(txt); } catch { return toast.error(t("al.p.badJson")); }
    try { await api.patch(`/studio/assets/${a.asset_id}`, { language: lang, content }); toast.success(t("al.p.contentSaved")); setTxt(null); onSaved(); } catch (e) { err(e, t); }
  };
  if (a.status !== "draft") return null;
  return txt === null ? <button onClick={() => setTxt(JSON.stringify(a.renders[lang], null, 2))} className="nb-tag bg-white text-xs" data-testid="rv-edit-content">{t("al.p.editContent")}</button> : (
    <div className="space-y-1" data-testid="rv-content-editor">
      <textarea className="nb-input font-mono text-xs" rows={10} dir="auto" value={txt} onChange={(e) => setTxt(e.target.value)} data-testid="rv-content-json" />
      <div className="flex gap-2"><button onClick={save} className="nb-btn-primary py-1 px-3 text-sm" data-testid="rv-content-save">{t("al.save")}</button><button onClick={() => setTxt(null)} className="nb-btn-outline py-1 px-3 text-sm">{t("al.cancel")}</button></div>
      <p className="text-[11px] text-gray-500">{t("al.p.editTrail")}</p>
    </div>
  );
}

export function VersionHistory({ a, onOpen }) {
  const { t } = useI18n();
  const [h, setH] = useState(null);
  useEffect(() => { api.get(`/studio/assets/${a.asset_id}/history`).then((r) => setH(r.data)).catch(() => {}); }, [a.asset_id, a.status]);
  const revise = async () => { try { const r = (await api.post(`/studio/assets/${a.asset_id}/revise`)).data; toast.success(t("al.p.revisionStarted", { v: r.version })); onOpen(r.asset_id); } catch (e) { err(e, t); } };
  return (
    <div className="text-xs space-y-1" data-testid="rv-history">
      <div className="flex items-center gap-2"><b className="flex items-center gap-1"><History className="w-3.5 h-3.5" /> {t("al.p.history")}</b>
        {a.status === "approved" && <button onClick={revise} className="nb-tag bg-white text-xs" data-testid="rv-revise"><GitBranch className="w-3 h-3 inline" /> {t("al.p.startRevision")}</button>}</div>
      {h?.versions.map((v) => <button key={v.asset_id} onClick={() => onOpen(v.asset_id)} className={`block underline ${v.asset_id === a.asset_id ? "font-bold" : ""}`} dir="ltr" data-testid={`rv-ver-${v.version}`}>v{v.version} · {t(`al.p.st.${v.status}`)} · {v.asset_id}</button>)}
      {h && <p className="text-gray-500">{t("al.p.histAssign", { n: h.assignments })}</p>}
    </div>
  );
}


export function WavePreview({ wave }) {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  const run = () => api.get(`/studio/wave/${encodeURIComponent(wave)}/rules/preview`).then((r) => setD(r.data)).catch((e) => err(e, t));
  return (
    <div className="nb-card bg-white p-3 mt-3 text-xs space-y-2" data-testid="wave-preview">
      <div className="flex items-center justify-between gap-2"><b>{t("al.ck2.wave")}</b><button onClick={run} className="nb-tag bg-white text-xs" data-testid="wave-preview-run">{t("al.ck2.wavePreview")}</button></div>
      {d && <>
        <p className="text-gray-600">{t("al.ck2.waveNote")} · {t("al.ck2.scanned", { n: d.drafts_scanned })} · {t("al.ck2.overlap", { n: d.overlapping.length })}</p>
        {d.rules.map((r) => (
          <details key={r.rule} className="border-t border-ink/10 pt-1" data-testid={`wave-rule-${r.rule}`}>
            <summary className="cursor-pointer"><b>{r.rule}</b> · {t("al.ck2.affected", { n: r.affected })} · {r.deterministic_fixes} {t("al.ck2.det")} / {r.interpretation_needed} {t("al.ck2.interp")}</summary>
            <p className="text-gray-600">{r.classification}</p>
            {r.examples.map((x, i) => <div key={i} className="mt-1 break-words"><code dir="ltr">{x.asset_id} · {x.path}</code><p dir="auto"><s>{String(x.before).slice(0, 300)}</s></p><p dir="auto" className="font-bold">{x.after ? String(x.after).slice(0, 300) : "—"}</p></div>)}
            <p className="text-gray-500" dir="auto">{r.assets.map((x) => x.title).join(" · ")}</p>
          </details>))}
      </>}
    </div>
  );
}
